import { useState } from 'react';
import { Button } from '../../components/common/Button';
import { Input } from '../../components/common/Input';
import { Modal } from '../../components/common/Modal';

interface CreateApiKeyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (key: string) => void;
}

export function CreateApiKeyModal({ isOpen, onClose, onSuccess }: CreateApiKeyModalProps) {
  const [name, setName] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [rateLimitRpm, setRateLimitRpm] = useState('60');
  const [rateLimitRps, setRateLimitRps] = useState('10');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [createdKey, setCreatedKey] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch('/api/api-keys', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
        },
        body: JSON.stringify({
          name,
          expires_at: expiresAt || undefined,
          rate_limit_rpm: parseInt(rateLimitRpm) || 60,
          rate_limit_rps: parseInt(rateLimitRps) || 10,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error?.message || '创建失败');
      }

      const apiKey = data.data?.key || data.key;
      setCreatedKey(apiKey);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setName('');
    setExpiresAt('');
    setRateLimitRpm('60');
    setRateLimitRps('10');
    setError('');
    if (createdKey) {
      onSuccess(createdKey);
    }
    setCreatedKey(null);
    onClose();
  };

  const copyToClipboard = () => {
    if (createdKey) {
      navigator.clipboard.writeText(createdKey);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={createdKey ? 'API Key 已创建' : '创建 API Key'}
      size="md"
    >
      {createdKey ? (
        <div className="space-y-4">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-sm text-yellow-800 font-medium mb-2">
              ⚠️ 请立即保存您的 API Key
            </p>
            <p className="text-sm text-yellow-700">
              这是您唯一一次看到此 Key 的机会。关闭此窗口后将无法再次查看。
            </p>
          </div>

          <div className="bg-gray-50 rounded-lg p-3">
            <code className="text-sm break-all">{createdKey}</code>
          </div>

          <div className="flex space-x-3">
            <Button onClick={copyToClipboard} variant="secondary" className="flex-1">
              复制到剪贴板
            </Button>
            <Button onClick={handleClose} className="flex-1">
              完成
            </Button>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <Input
            label="名称"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="例如：生产环境 Key"
            required
          />

          <Input
            label="过期时间（可选）"
            type="datetime-local"
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
          />

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="每分钟请求数限制"
              type="number"
              value={rateLimitRpm}
              onChange={(e) => setRateLimitRpm(e.target.value)}
            />
            <Input
              label="每秒请求数限制"
              type="number"
              value={rateLimitRps}
              onChange={(e) => setRateLimitRps(e.target.value)}
            />
          </div>

          <div className="flex space-x-3 pt-4">
            <Button type="button" onClick={handleClose} variant="secondary" className="flex-1">
              取消
            </Button>
            <Button type="submit" loading={loading} className="flex-1">
              创建
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
}
